/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import mikeSharedTheme from "../../styles/mikeSharedTheme";
import { useCallback, useEffect, useMemo } from "react";
import MikeButton from "../mike-button/MikeButton";
import { Formik, Form, } from 'formik'
import TextField from './../formik/TextField'
import * as Yup from "yup";
import SidePanel from "../SidePanel";
import { useTypedSelector } from "../../reducers";
import Typography from "@mui/material/Typography";
import { StyledSelect } from "../formik/StyledSelect";
import { actionFieldIsValid, EU_ASPECT_TYPES, getActionField, getAreaDisplayName, getMeasureType, getStatusImplementation, ISelectableValue, ISelection, measureTypeIsValid } from "../../model/measure";
import MikeStickyPanel from "../mike-sticky-panel";
import { MikeStickyPanelHeaderContainer } from "../mike-sticky-panel/MikeStickyPanelHeaderContainer";
import { MikeStickyPanelBottomActions } from "../mike-sticky-panel/MikeStickyPanelBottomActions";
import { IMeasureFormValue } from "../../model/IMeasure";
import { measureSet } from "../../reducers/measures";
import { useAppDispatch } from "../../store/store";
import { drawingEnabled, showNewLegendLayer } from "../../reducers/legend";
import InputAdornment from "@mui/material/InputAdornment";
import { inputBaseClasses } from "@mui/material/InputBase";
import { backendApi, IMeasureLocation, useCreateMeasureMutation, useEditMeasureMutation } from "../../apis/apiSlice";
import { errorAdded, messageAdded } from "../../reducers/toast";
import { getTranslatedErrorMessage } from "../../helpers/errortranslations";
import { NEW_MEASURES_ID } from "../../model/layerGroups";
import MikeVisualizerLib from "../../MikeVisualizer/lib/MikeVisualizer";
import { makeDefaultVectorStyle, MAP_COLORS } from "../ViewerLegend/utils";
import AutoListbox from "../formik/AutoListbox";

interface IProps {   
  areaNames: Array<string>;
  projectDevelopers: Array<ISelectableValue>;
  onCancel?: () => void;
  onOk?: () => void;
}


const containerStyle = css` 
  padding: ${mikeSharedTheme.spacing(1)};  
`

const buttonContainerStyle = css` 
  display: flex;
  justify-content: center;
  margin-bottom: ${mikeSharedTheme.spacing(2)}; 
`
const inputAdornmenttyle = (disabled = false) => {
  const color = disabled ? mikeSharedTheme.palette.mediumGrey?.main : mikeSharedTheme.palette.primary.main
  return css` 
  color: ${color};  
`
} 

const flexContainerStyle = css` 
  display: flex;
  justify-content: space-between;  
`

const validationSchema = Yup.object().shape({ 
  x: Yup.number().typeError('X muß numerisch sein').required("X ist erforderlich"),
  y: Yup.number().typeError('Y muß numerisch sein').required("Y ist erforderlich"),
  name: Yup.string().required("Name ist erforderlich").max(300, "Name ist länger als 300 Zeichen"),
  description: Yup.string().required("Beschreibung ist erforderlich").max(2700, "Beschreibung ist länger als 2700 Zeichen"),
  sourceTableName: Yup.string().required("Zuständigkeitsgebiet ist erforderlich"),
  projectDeveloper: Yup.string().required("Vorhabenträger ist erforderlich"),
  euAspect: Yup.number().required("EU-Aspekt ist erforderlich"),
  actionField: Yup.number().required("Handlungsfeld ist erforderlich").test({
      name: 'validactionfield',
      exclusive: false,
      params: { },
      message: 'Handlungsfeld ist erforderlich',
      test: (value, context) => actionFieldIsValid(context.parent.euAspect, value),
    }),
  measureType: Yup.string().required("Maßnahmentyp ist erforderlich").test({
      name: 'validmeasuretype',
      exclusive: false,
      params: { },
      message: 'Maßnahmentyp ist erforderlich',
      test: (value, context) => measureTypeIsValid(context.parent.actionField, value),
    }),
  implementationStatus: Yup.string().required("Status ist erforderlich")
});

const MeasureDialog = (props: IProps) => {
  const { 
    areaNames, 
    projectDevelopers, 
    onCancel,
    onOk
  } = props;

    const dispatch = useAppDispatch();
    const [createMeasure, { isLoading: creatingMeasure} ] = useCreateMeasureMutation();
    const [editMeasure] = useEditMeasureMutation();

    // const pd = useMemo(( ) => {return projectDevelopers.map(p => p.displayName)},[projectDevelopers])

    const { measureToEdit: measure, coordinates, measureDialogOpen: open } = useTypedSelector((state) => state.measures);

    const {initialX, initialY } = useMemo(() => {
      if (coordinates && coordinates.length === 2){
        return {initialX: coordinates[0].toFixed(2), initialY: coordinates[1].toFixed(2) }
      }
      else{
        return {initialX: "", initialY: "" }
      }
    },[coordinates])


  const initialVals = useMemo(() => {
    if (measure){
      const coords = measure.point && measure.point.coordinates ? measure.point.coordinates : [];
      const xCoord = coords.length > 0 ? coords[0].toFixed() : "";
      const yCoord = coords.length > 1 ? coords[1].toFixed() : "";
      const euaspect = measure.euAspect ? measure.euAspect.toString() : "";
      const actionField =  measure.actionField ? measure.actionField.toString() : "";
      const area = measure.sourceTableName && measure.sourceTableName !== "placeholder" ?   measure.sourceTableName : "";      
      return {x: xCoord, y: yCoord, name: measure.name, description: measure.description,  sourceTableName: area, projectDeveloper: measure.projectDeveloper ? measure.projectDeveloper : "", euAspect: euaspect, actionField: actionField, measureType: measure.measureType, implementationStatus: measure.implementationStatus}
    } else{
      const areaName = areaNames.length === 1 ? areaNames[0] : "";
      return { x: initialX, y: initialY, sourceTableName: areaName, name: "", description: "", projectDeveloper: "", euAspect: "", actionField: "", measureType: "", implementationStatus: ""}
    } 
  }, [initialX, initialY, measure, areaNames])

  const areaDisplayValues = useMemo(() => {
    if (areaNames.length > 0){
      return areaNames.map((name: string) => {return {id: name, displayName: getAreaDisplayName(name) }})
    }
    return [];
  }, [areaNames])

  useEffect(() => {
    dispatch(drawingEnabled(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const clearDrawnMeasure = () => {
    const {delete2DData} = MikeVisualizerLib;
    delete2DData(NEW_MEASURES_ID);
    dispatch(showNewLegendLayer(false));    
  }

  const onSubmit = useCallback(async (values: IMeasureFormValue) => {    
    let canSave = true;  
    if (!values.euAspect){
      canSave = false;
      dispatch(errorAdded(["Bitte wählen Sie den EU-Aspekt aus."]))
    }
    if (!values.actionField){
      canSave = false;
      dispatch(errorAdded(["Bitte wählen Sie das Handlungsfeld aus."]))
    }

    if (!actionFieldIsValid(Number(values.euAspect), Number(values.actionField))){
      canSave = false;
      dispatch(errorAdded(["Bitte korrigieren Sie das Handlungsfeld."]))
      return;
    } 

    if (!values.sourceTableName){
      canSave = false;
      dispatch(errorAdded(["Bitte wählen Sie das Zuständigkeitsgebiet aus."]))
      
    }
     if (!values.measureType){
      canSave = false;
      dispatch(errorAdded(["Bitte wählen Sie den Maßnahmentyp aus."]))
    }  

    if (!measureTypeIsValid(Number(values.actionField), values.measureType)){
      canSave = false;
      dispatch(errorAdded(["Bitte korrigieren Sie den Maßnahmentyp."]))
    }    

    if (!canSave){
      return;
    }

    if (measure){
      try{
        const response = await editMeasure({...values, x: parseFloat(values.x), y: parseFloat(values.y), measureId: measure.id} )
        if (response.error){
          const error = response.error as any;
          const detail = error.data && error.data.detail ? getTranslatedErrorMessage(error.data.detail) : "Das Ändern der Maßnahme ist fehlgeschlagen.";         
          dispatch(errorAdded([detail]))
        }
        else{
          dispatch(messageAdded(["Die Änderung der Maßnahme wurde erfolgreich gespeichert."]));
           dispatch(measureSet(null))
           if (onOk){
            onOk();
          }
        }
      }
       catch{
        dispatch(errorAdded(["Das Ändern der Maßnahme ist fehlgeschlagen."]))
      }
      
    }
    else{
      try{
        const response = await createMeasure({...values, x: parseFloat(values.x), y: parseFloat(values.y)} ) 
        if (response.error){
          const error = response.error as any;
          const detail = error.data && error.data.detail ? getTranslatedErrorMessage(error.data.detail) : "Das Erzeugen der Maßnahme ist fehlgeschlagen.";     
          dispatch(errorAdded([detail]))
        }
        else{
          dispatch(messageAdded(["Die Maßnahme wurde erfolgreich erzeugt."]))
           dispatch(measureSet(null))
           if (onOk){
            onOk();
          }
        }
      }
      catch{
        dispatch(errorAdded(["Das Erzeugen der Maßnahme ist fehlgeschlagen."]))
      }
      finally{
        clearDrawnMeasure();
      }      
    }  
   
  }, [dispatch, onOk, measure])

  const handleCancel = useCallback(() => {    
    clearDrawnMeasure();
    dispatch(measureSet(null))
    if (onCancel){
      onCancel();
   }
   }, [dispatch, onCancel])

  const VALUEFIELD = "id";
  const PRIMARYFIELD ="displayName";

  const actionFields = useCallback((values: IMeasureFormValue) => {
    if (values.euAspect){
      return getActionField(Number(values.euAspect))
    }
    else{
      return  Array<ISelection>();
    }    
  }, [])

  const measureTypes = useCallback((values: IMeasureFormValue) => {
    if (values.euAspect && values.actionField){
      const actionField = getActionField(Number(values.euAspect))
      // Reset measure type when eu aspect changed     
      const actionIds = actionField.map((action: ISelection) => action.id);
      if (!actionIds.includes(Number(values.actionField))){
        return Array<ISelection>();
      }      
      return getMeasureType(Number(values.actionField))      
    }
    else{
      return  Array<ISelection>();
    } 
  },[])

  const implementationStatuses = useCallback((values: IMeasureFormValue) => {
    if (values.actionField){
      return getStatusImplementation(Number(values.actionField))      
    }
    else{
      return  Array<ISelection>();
    } 
  },[])

  const {disableEditing, disableEditingCoordsNameDesc, disableEditImplementationStatus} = useMemo(() => {    
    const disableFirstFourColumnsForNonAdminUsers = () => {
      // Enable columns when creating new measure or edit measures from third cycle
      if (measure === null || measure.createdInCycle === 3){
        return false; 
      }       
      return measure.createdBy === null; // Backend fills createdBy for admin users only
    }
    return {
      disableEditing: measure !== null && measure.createdInCycle < 3, // Enable columns when creating new measure or edit measures from third cycle
      disableEditingCoordsNameDesc: disableFirstFourColumnsForNonAdminUsers(), // Enable editing columns from cycle 1 or 2 for admin users only    
      disableEditImplementationStatus: measure !== null &&  measure.createdInCycle < 3 && ['COM'].includes(measure.implementationStatus) // Eanble editing implementation status from cycle 1 or 2 when implementation is not 'COM' (abgeschlossen)
    }
  },[measure])
  
  const handleCheckLocation =  useCallback(async (vals: IMeasureFormValue) => {
    if (vals.x && vals.y && vals.sourceTableName){
      const notDrawnInMap = initialVals.x === "" || initialVals.y === "";      
      //Update purple dot on map
      const updatedFC = {
          type: "FeatureCollection",
          features: [
              {
                  type: "Feature",
                  geometry: {
                      type: "Point",
                      coordinates: [
                          parseFloat(vals.x),
                          parseFloat(vals.y)
                      ]
                  },
                  properties: {
                      id: "Koordinateneingabe"
                  },
                  id: "Koordinateneingabe"
              }
          ]
      }
      const { update2DData } = MikeVisualizerLib;
      await update2DData(updatedFC as any, NEW_MEASURES_ID, undefined, undefined, 1, makeDefaultVectorStyle(MAP_COLORS.LILAC));  
      dispatch(showNewLegendLayer(true));    
      // Check if dot is within selected area
      const payload = {x: parseFloat(vals.x), y: parseFloat(vals.y), sourceTableName: vals.sourceTableName} as IMeasureLocation;
      const response = await dispatch(backendApi.endpoints.checkMeasure.initiate(payload)).unwrap()
      if (!response.hasAccess){        
        if (areaNames.length > 1){
          if (notDrawnInMap){
            dispatch(errorAdded(["Die Maßnahme liegt nicht im gewählten Zuständigkeitsgebiet.", "Bitte wählen Sie das richtige Zuständigkeitsgebiet", "oder passen Sie die Koordinaten an."]))
          }
          else{
            dispatch(errorAdded(["Die Maßnahme liegt nicht im gewählten Zuständigkeitsgebiet.", "Bitte wählen Sie das richtige Zuständigkeitsgebiet", "oder schließen Sie diesen Dialog und erzeugen Sie die Maßnahme erneut."]))
          }          
        }
        else{
          if (notDrawnInMap){
            dispatch(errorAdded(["Die Maßnahme liegt nicht im gewählten Zuständigkeitsgebiet.", "Bitte passen Sie die Koordinaten an."]))
          }
          else{
            dispatch(errorAdded(["Die Maßnahme liegt nicht im gewählten Zuständigkeitsgebiet.", "Bitte schließen Sie diesen Dialog und erzeugen Sie die Maßnahme erneut."]))
          }          
        }        
      }
      else{
        dispatch(messageAdded(["Die Maßnahme kann im gewählten Zuständigkeitsgebiet erzeugt werden."]))
      }
    }    
  }, [dispatch, areaNames, initialVals])

return (
  <SidePanel open={open}>  
  <MikeStickyPanel>
  <div css={containerStyle}>
    <MikeStickyPanelHeaderContainer>
       <Typography sx={{pb: 2, pt: 2}} variant="h5">{"Maßnahmen-Erfassung"}</Typography>
      </MikeStickyPanelHeaderContainer>
      
      <Formik
        validationSchema={validationSchema}
        initialValues={initialVals}
        enableReinitialize     
        onSubmit={onSubmit}>
           {(formikProps) => {
            const { handleChange, setFieldTouched, handleSubmit, setFieldValue, isValid, values } = formikProps
        
            const handleOnChange = (name, e) => {            
              handleChange(e)
              setFieldTouched(name, true, false)              
            }

            const handleAutocompleteOnChange = (e, sel) => {
              setFieldValue('projectDeveloper', sel)
              handleChange(e)                    
            } 
  
            return (           
              <Form>  
                <div css={flexContainerStyle}> 
                  <TextField             
                    onChange={handleOnChange.bind(null, 'x')}
                    disabled={disableEditingCoordsNameDesc}
                    name='x'        
                    label={'X Koordinate'}                                        
                    addPlaceHolder={true}
                    fullWidth
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment sx={{
                                                
                          opacity: 0,
                          pointerEvents: 'none',
                          [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                            opacity: 1,
                          },
                        }} position="start"><div css={inputAdornmenttyle(disableEditing)}>32</div></InputAdornment>,
                      },
                    }}
                  />   
                  <TextField
                    onChange={handleOnChange.bind(null, 'y')}
                    disabled={disableEditingCoordsNameDesc}
                    name='y'        
                    label={'Y Koordinate'}                                        
                    addPlaceHolder={true}
                    fullWidth
                  />
               
                </div>
                    <div >
                    <Typography variant="body2">{"Maßnahme liegt im Zuständigkeitsgebiet"}</Typography>                   
                    <StyledSelect             
                      fullWidth={true}
                      disabled={disableEditingCoordsNameDesc}                        
                      name={'sourceTableName'}                          
                      valueField={VALUEFIELD}
                      primaryField={PRIMARYFIELD}
                      items={areaDisplayValues}
                      onChange={handleOnChange.bind(null, 'sourceTableName')}                      
                    />                  
                  </div> 
               
                  <div css={buttonContainerStyle}>
                    <MikeButton onClick={() => handleCheckLocation(values)} variant="outlined" disabled={!values.sourceTableName}>
                      {"Lage der Koordinaten prüfen"}
                    </MikeButton>
                  </div>                                    
                  
                  <TextField
                    onChange={handleOnChange.bind(null, 'name')}
                    disabled={disableEditingCoordsNameDesc}
                    name='name'        
                    label={'Kurzbezeichnung'}                                        
                    addPlaceHolder={true}
                    fullWidth
                  />   
  
                  <TextField
                    onChange={handleOnChange.bind(null, 'description')}
                    disabled={disableEditingCoordsNameDesc}
                    name='description'        
                    label={'Beschreibung'}                                        
                    addPlaceHolder={true}
                    type="textarea"
                    rows={4}
                    fullWidth
                  />        

                    <div>
                    <Typography variant="body2">{"Vorhabenträger"}</Typography>                   
                    <AutoListbox  
                      label={'Vorhabenträger'}
                      addPlaceHolder={true}   
                      disabled={disableEditing}          
                      fullWidth={true}
                      name={'projectDeveloper'}  
                      options={projectDevelopers}
                      onChange={(e, val) => handleAutocompleteOnChange(e, val)}
                    />                  
                  </div> 

                  <div>
                    <Typography variant="body2">{"EU-Aspekt"}</Typography>                   
                    <StyledSelect   
                      disabled={disableEditing}          
                      fullWidth={true}
                      name={'euAspect'}                          
                      valueField={VALUEFIELD}
                      primaryField={PRIMARYFIELD}
                      items={EU_ASPECT_TYPES}
                      onChange={handleOnChange.bind(null, 'euAspect')}                     
                    />                  
                  </div> 
  
                  <div>
                    <Typography variant="body2">{"Handlungsfeld"}</Typography>                   
                    <StyledSelect    
                      disabled={disableEditing}           
                      fullWidth={true}
                      name={'actionField'}                          
                      valueField={VALUEFIELD}
                      primaryField={PRIMARYFIELD}
                      items={actionFields(values)}
                      onChange={handleOnChange.bind(null, 'actionField')}                     
                    />                  
                  </div>  
  
                  <div >
                    <Typography variant="body2">{"Maßnahmentyp"}</Typography>                   
                    <StyledSelect 
                      disabled={disableEditing}              
                      fullWidth={true}
                      name={'measureType'}                          
                      valueField={VALUEFIELD}
                      primaryField={PRIMARYFIELD}
                      items={measureTypes(values)}
                      onChange={handleOnChange.bind(null, 'measureType')}                      
                    />                  
                  </div>  
                  <div >
                    <Typography variant="body2">{"Umsetzungsstatus"}</Typography>                   
                    <StyledSelect             
                      fullWidth={true}
                      disabled={disableEditImplementationStatus}                        
                      name={'implementationStatus'}                          
                      valueField={VALUEFIELD}
                      primaryField={PRIMARYFIELD}
                      items={implementationStatuses(values)}
                      onChange={handleOnChange.bind(null, 'implementationStatus')}                      
                    />                  
                  </div>        
                  <MikeStickyPanelBottomActions>
                  <MikeButton size="small" onClick={handleCancel} variant="text">
                    {"Abbrechen"}
                  </MikeButton>
                    <MikeButton variant="contained" disabled={!isValid} id="createFolderButton" onClick={() => handleSubmit()} active={creatingMeasure}>
                      {'Speichern'}               
                    </MikeButton> 
                  </MikeStickyPanelBottomActions>           
                </Form>
          )}}
        </Formik>        
      </div>
      </MikeStickyPanel>
    </SidePanel>
)
}

export default MeasureDialog

